import { useEffect, useMemo, useState } from 'react'
import type { PartAttachmentsService } from '../attachments/usePartAttachments'
import {
  type EditPartConfigRequest,
  type EditPartConfigService,
  defaultEditPartConfigService,
} from '../edit-part-config-service'
import type { LibraryPartRow, PartDetail } from '../types'
import { PartConfiguratorShell } from './PartConfiguratorShell'
import type { PartConfiguratorFormState, PartConfiguratorSaveSeam } from './part-configurator-save'
import { PART_LIBRARY_CAPABILITIES, fromLibraryPartRow } from './part-configurator-subject'

export interface EditConfigurationModalProps {
  readonly open: boolean
  readonly onClose: () => void
  /** `PartDetail` extends `LibraryPartRow` — the same modal opens from
   * either the Part detail page (which has the fuller `PartDetail`) or a
   * Parts Library table row (which only has `LibraryPartRow`); every field
   * this modal reads is on the shared base shape. */
  readonly part: LibraryPartRow
  /** Called with the refreshed `PartDetail` once a save succeeds — the
   * caller's own listing/detail state is NOT mutated by this modal; it owns
   * refreshing itself (e.g. `PartDetailPage` calls its `refetch`,
   * `PartsLibraryPage` calls its own `refetch`) so this component never
   * assumes which data shape the caller holds. */
  readonly onSaved?: (refreshed: PartDetail) => void
  /** Injectable for tests / future service swap. */
  readonly service?: EditPartConfigService
  /** UID-46 — "Apply to multiple parts" applies THIS modal's current,
   * in-progress configuration to the caller's OWN active multi-selection,
   * via the SAME loop-PATCH bulk service `PartsBulkConfigureModal` uses.
   * Absent, the button renders disabled with a text explanation. */
  readonly onApplyToMultiple?: (request: EditPartConfigRequest) => void
  /** Shows the "Apply to multiple parts" button in its loading state while
   * the caller's batch (triggered by `onApplyToMultiple` above) is
   * in-flight — independent of this modal's OWN `saving` state. */
  readonly applyToMultipleBusy?: boolean
  /** UID-225 — this modal has no owned cart-mutation logic of its own;
   * absent, the "Add to cart" CTA renders disabled with a text
   * explanation rather than silently inert or fabricating a mutation. */
  readonly onAddToCart?: (part: LibraryPartRow) => void
  /** UID-225 — same disabled-with-title pattern as `onAddToCart` above. */
  readonly onSendToRfq?: (part: LibraryPartRow) => void
  /** Forwarded to the embedded `PartAttachmentsSection` (Drawing analysis
   * tab) — injectable for tests; defaults to its own real BFF-backed
   * service when omitted. */
  readonly attachmentsService?: PartAttachmentsService
}

/**
 * UID-434/WS-A — the Part Library entry point. This is now a THIN ADAPTER
 * around the shared `PartConfiguratorShell`: it maps `LibraryPartRow` onto
 * the vertical-agnostic `PartConfiguratorSubject` (`fromLibraryPartRow`),
 * supplies Part Library's own unchanged capability set
 * (`PART_LIBRARY_CAPABILITIES` — "Apply to multiple parts" shown, DFM
 * capable, attachments capable), and wraps the EXACT same save-then-reprice
 * seam this component always used (`service.editPartConfig`, `PATCH
 * /parts/{partId}` by default) behind the shell's generic
 * `PartConfiguratorSaveSeam` interface. Per ADR-0117 D9.1 and this ticket's
 * human D4 ruling, this seam is NOT changed or unified with any other
 * vertical's seam — only wrapped so the shell can call it without knowing
 * its shape.
 *
 * Every prop, and every externally-observable behavior, is unchanged from
 * the pre-extraction component — see `EditConfigurationModal.test.tsx`
 * (unmodified by this extraction) for the characterization proof.
 */
export function EditConfigurationModal({
  open,
  onClose,
  part,
  onSaved,
  service = defaultEditPartConfigService,
  onApplyToMultiple,
  applyToMultipleBusy = false,
  onAddToCart,
  onSendToRfq,
  attachmentsService,
}: EditConfigurationModalProps) {
  // The shell's OWN `initial`-seeding memo depends on `subject` alone (not
  // on any post-save refresh) — mirroring the pre-extraction component's
  // `initial` depending on the STABLE `part` PROP, never on `shownPart`
  // (`refreshedPart ?? part`). That distinction is preserved here: `subject`
  // is memoized off `part` alone, so a successful "Save this configuration"
  // (which does not close the modal) can never re-trigger the shell's
  // field-reset effect and silently discard in-progress edits/UI state.
  const subject = useMemo(() => fromLibraryPartRow(part), [part])

  // Tracked ONLY so `onAddToCart`/`onSendToRfq` (below) can be called with
  // the same "freshest known PartDetail" the pre-extraction component used
  // (`shownPart = refreshedPart ?? part`) — the shared shell's own generic
  // `PartConfiguratorSubject` can't carry every `LibraryPartRow`/
  // `PartDetail` field those callback's real (future) consumers may need,
  // so this wrapper keeps its own mirror, updated in the same tick the save
  // seam succeeds.
  const [refreshedPart, setRefreshedPart] = useState<PartDetail | null>(null)
  // `part` isn't read inside the effect body — it's there so a DIFFERENT
  // part opening while the modal stays mounted also clears the stale
  // mirror, mirroring the shell's own `[open, initial]` reset-effect intent
  // (see `PartConfiguratorShell`'s matching comment).
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above.
  useEffect(() => {
    if (!open) return
    setRefreshedPart(null)
  }, [open, part])
  const shownPart = refreshedPart ?? part

  const saveSeam: PartConfiguratorSaveSeam = useMemo(
    () => ({
      async save(form: PartConfiguratorFormState) {
        const request: EditPartConfigRequest = formToEditPartConfigRequest(form)
        const outcome = await service.editPartConfig(part.sourceable.id, request)

        if (outcome.ok) {
          setRefreshedPart(outcome.part)
          onSaved?.(outcome.part)
          return { ok: true as const, subject: fromLibraryPartRow(outcome.part) }
        }
        if (outcome.kind === 'field_errors') {
          return { ok: false as const, kind: 'field_errors' as const, errors: outcome.errors }
        }
        return { ok: false as const, kind: 'error' as const, message: outcome.message }
      },
    }),
    [part, service, onSaved],
  )

  return (
    <PartConfiguratorShell
      open={open}
      onClose={onClose}
      subject={subject}
      capabilities={PART_LIBRARY_CAPABILITIES}
      saveSeam={saveSeam}
      applyToMultipleBusy={applyToMultipleBusy}
      {...(onApplyToMultiple
        ? {
            onApplyToMultiple: (form: PartConfiguratorFormState) =>
              onApplyToMultiple(formToEditPartConfigRequest(form)),
          }
        : {})}
      {...(onAddToCart ? { onAddToCart: () => onAddToCart(shownPart) } : {})}
      {...(onSendToRfq ? { onSendToRfq: () => onSendToRfq(shownPart) } : {})}
      {...(attachmentsService ? { attachmentsService } : {})}
    />
  )
}

/** `PartConfiguratorFormState`'s fields are field-for-field the same
 * information `EditPartConfigRequest` carries (see that type's own doc
 * comment) — this is the one place that mapping happens, so the Parts
 * seam's wire shape can never silently drift from the shell's generic
 * form-state shape. */
function formToEditPartConfigRequest(form: PartConfiguratorFormState): EditPartConfigRequest {
  return {
    process: form.process,
    quantity: form.quantity,
    material: form.material,
    finish: form.finish,
    color: form.color,
    threadedHoleCount: form.threadedHoleCount,
    inspections: form.inspections ? [...form.inspections] : null,
    certificates: form.certificates ? [...form.certificates] : null,
    additionalRequirements: form.additionalRequirements,
  }
}
